import { useRef } from 'react';

import { type LTLItem } from '../../types';

const ITEM_COLOR_PALETTE = [
  '#F9C74F',
  '#43AA8B',
  '#9B5DE5',
  '#277DA1',
  '#F15BB5',
  '#90BE6D',
  '#F3722C',
  '#3B82F6',
  '#A3E635',
  '#A855F7',
  '#00F5D4',
  '#EF4444',
  '#577590',
  '#EC4899',
  '#84CC16',
  '#6366F1',
  '#F8961E',
  '#14B8A6',
  '#FB7185',
  '#00BBF9',
  '#F59E0B',
  '#4D908E',
  '#22C55E',
] as const;

function hashItemId(itemId: string): number {
  let hash = 0;

  for (let i = 0; i < itemId.length; i += 1) {
    hash = (hash << 5) - hash + itemId.charCodeAt(i);
    hash |= 0;
  }

  return Math.abs(hash);
}

function normalizeItemIds(itemIds: string[]): string[] {
  return Array.from(
    new Set(itemIds.map((itemId) => itemId?.trim()).filter(Boolean))
  );
}

function getPaletteColorByStableIndex(itemId: string): string {
  return ITEM_COLOR_PALETTE[hashItemId(itemId) % ITEM_COLOR_PALETTE.length];
}

export function buildItemColorMap(
  itemIds: string[]
): ReadonlyMap<string, string> {
  const itemColorMap = new Map<string, string>();

  const normalizedItemIds = normalizeItemIds(itemIds).sort(
    (leftId, rightId) => {
      const leftHash = hashItemId(leftId);
      const rightHash = hashItemId(rightId);
      if (leftHash !== rightHash) {
        return leftHash - rightHash;
      }

      return leftId.localeCompare(rightId);
    }
  );

  const usedPaletteIndexes = new Set<number>();

  normalizedItemIds.forEach((itemId) => {
    const startingIndex = hashItemId(itemId) % ITEM_COLOR_PALETTE.length;
    let paletteIndex = startingIndex;

    for (let offset = 0; offset < ITEM_COLOR_PALETTE.length; offset += 1) {
      const candidateIndex =
        (startingIndex + offset) % ITEM_COLOR_PALETTE.length;

      if (!usedPaletteIndexes.has(candidateIndex)) {
        paletteIndex = candidateIndex;
        break;
      }
    }

    usedPaletteIndexes.add(paletteIndex);
    itemColorMap.set(itemId, ITEM_COLOR_PALETTE[paletteIndex]);
  });

  return itemColorMap;
}

export function getColorForItemId(
  itemId: string,
  itemColorMap?: ReadonlyMap<string, string>
): string {
  if (!itemId) {
    return ITEM_COLOR_PALETTE[0];
  }

  const mappedColor = itemColorMap?.get(itemId);

  if (mappedColor) {
    return mappedColor;
  }

  return getPaletteColorByStableIndex(itemId);
}

export function useStableItemIds(items: LTLItem[]): string[] {
  const itemIds = items.map((item) => item.itemId);
  const previousItemIdsRef = useRef<string[]>([]);
  const previousItemIds = previousItemIdsRef.current;

  const hasSameItemIds =
    previousItemIds.length === itemIds.length &&
    previousItemIds.every((itemId, index) => itemId === itemIds[index]);

  if (hasSameItemIds) {
    return previousItemIds;
  }

  previousItemIdsRef.current = itemIds;
  return itemIds;
}
